import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { getFamilySlugForHost } from "../lib/families";
import { writeCachedBranding } from "../lib/brandingCache";

interface PublicBranding {
  slug: string;
  name: string;
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
}

/**
 * Fetches family branding (name, logo URLs) for the current hostname.
 * Used by surfaces that render before sign-in (login, invite preview)
 * so they pick up admin-uploaded logos. Returns null while loading or
 * when the host isn't mapped to a family.
 *
 * Auth-side users should still prefer membership data from /me; that
 * already includes the same fields and avoids one extra request.
 *
 * Side effect: writes the URLs to localStorage so the next page load
 * paints the right logo on the very first frame (see brandingCache).
 */
export function usePublicBranding(): PublicBranding | null {
  const slug = getFamilySlugForHost();

  const query = useQuery<PublicBranding>({
    queryKey: ["public-branding", slug],
    queryFn: () =>
      apiFetch<PublicBranding>(`/public/families/${slug}/branding`),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (query.data) {
      writeCachedBranding(slug, {
        logoLightUrl: query.data.logoLightUrl,
        logoDarkUrl: query.data.logoDarkUrl,
      });
    }
  }, [slug, query.data]);

  return query.data ?? null;
}
